import { useState, useEffect, useRef, useCallback } from 'react';
import { signOut } from 'aws-amplify/auth';
import { userAppContext } from '../../context/Context';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Bars3Icon } from '@heroicons/react/24/solid';
import { useAuthStore } from '../../store/authStore';
import { NAV_PRIMARY_LINKS, type NavPrimaryLink } from './topNavigationHelpers';
import { NAV_TOOLS_MENU, type NavMegaMenuId } from './navMenuConfig';
import NavMegaPanel from './NavMegaPanel';
import DocsMegaMenu from './DocsMegaMenu';
import ToolsMegaMenu from './ToolsMegaMenu';
import MobileNavDrawer from './MobileNavDrawer';
import useText from '@/hooks/useText';
import { slugifyTrackingValue, trackEvent } from '@/utils/analytics';

type TopRightMenuProps = {
  renderizedBy: string;
};

const TopRightMenu = ({ renderizedBy }: TopRightMenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const labelAbout = useText('navigation.about');
  const labelDocs = useText('navigation.docs');
  const labelTools = useText('navigation.tools');
  const labelPricing = useText('navigation.pricing');

  const linkLabels: Record<string, string> = {
    'navigation.about': labelAbout,
    'navigation.docs': labelDocs,
    'navigation.tools': labelTools,
    'navigation.pricing': labelPricing,
    'navigation.profileArea': 'Profile Area',
  };

  const { isAuthenticated, setIsAuthenticated, setGroups, setAuthorization } = userAppContext();
  const user = useAuthStore(state => state.user);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMega, setActiveMega] = useState<NavMegaMenuId | null>(null);
  const [isUserDropdownOpen, setUserDropdownOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const topRightMenuRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const megaCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeMega = useCallback(() => {
    if (megaCloseTimerRef.current) {
      clearTimeout(megaCloseTimerRef.current);
      megaCloseTimerRef.current = null;
    }
    setActiveMega(null);
  }, []);

  const openMegaMenu = useCallback((id: NavMegaMenuId) => {
    if (megaCloseTimerRef.current) {
      clearTimeout(megaCloseTimerRef.current);
      megaCloseTimerRef.current = null;
    }
    setActiveMega(id);
  }, []);

  const scheduleCloseMegaMenu = useCallback(() => {
    if (megaCloseTimerRef.current) clearTimeout(megaCloseTimerRef.current);
    megaCloseTimerRef.current = setTimeout(() => {
      setActiveMega(null);
      megaCloseTimerRef.current = null;
    }, 140);
  }, []);

  const cancelCloseMegaMenu = useCallback(() => {
    if (megaCloseTimerRef.current) {
      clearTimeout(megaCloseTimerRef.current);
      megaCloseTimerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      if (megaCloseTimerRef.current) clearTimeout(megaCloseTimerRef.current);
    },
    [],
  );
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const handleNavigate = useCallback(() => {
    closeMega();
    closeMobileMenu();
  }, [closeMega, closeMobileMenu]);

  useEffect(() => {
    closeMega();
    closeMobileMenu();
  }, [location.pathname, closeMega, closeMobileMenu]);

  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 992);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  useEffect(() => {
    if (!isDesktop) closeMega();
  }, [isDesktop, closeMega]);

  useEffect(() => {
    if (!isMobileMenuOpen || isDesktop) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('top-nav-mobile-open');
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove('top-nav-mobile-open');
    };
  }, [isMobileMenuOpen, isDesktop]);

  const handleLogout = () => {
    trackEvent({
      name: 'navigation_click',
      buttonId: 'navigation_logout',
      label: 'Logout',
      page: 'global',
      section: 'top_navigation',
      feature: 'navigation',
      eventCategory: 'navigation',
    });
    closeMobileMenu();
    setIsAuthenticated(false);
    setGroups([]);
    setAuthorization('');
    signOut();
    navigate('/');
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('.top-nav-mobile') || target.closest('.top-nav-mobile-backdrop')) {
      return;
    }
    if (topRightMenuRef.current && !topRightMenuRef.current.contains(target)) {
      setMobileMenuOpen(false);
      closeMega();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  });

  const handleUserClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (
      userDropdownRef.current &&
      !userDropdownRef.current.contains(target) &&
      topRightMenuRef.current &&
      !topRightMenuRef.current.contains(target)
    ) {
      setUserDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleUserClickOutside);
    return () => document.removeEventListener('mousedown', handleUserClickOutside);
  }, []);

  const trackNavClick = (link: NavPrimaryLink) => {
    trackEvent({
      name: 'navigation_click',
      buttonId: `navigation_route_${slugifyTrackingValue(link.href)}`,
      label: linkLabels[link.cmsKey] ?? link.label,
      page: 'global',
      section: 'top_navigation',
      feature: 'navigation',
      eventCategory: 'navigation',
    });
  };

  const userDisplay = user?.email || user?.username || user?.sub || '';
  const visibleLinks = NAV_PRIMARY_LINKS.filter(link => !link.showOnlyMobile || !isDesktop);
  const isApplyPage = renderizedBy === 'ApplyPage';
  const navIconClass = isApplyPage ? 'custom-nav-menu' : '';
  const userRoles: string[] = user?.['cognito:groups'] ?? [];
  const visibleTools = NAV_TOOLS_MENU.filter(tool => {
    if (!tool.roles) return true;
    return tool.roles.some(role => userRoles.includes(role));
  });

  const renderPrimaryLink = (link: NavPrimaryLink) => {
    const label = linkLabels[link.cmsKey] ?? link.label;
    const isMegaOpen = link.megaMenu && activeMega === link.megaMenu;

    if (link.megaMenu) {
      return (
        <span
          className={`top-nav-trigger ${isMegaOpen ? 'top-nav-trigger--active' : ''}`}
          aria-expanded={isMegaOpen}
          aria-haspopup="true"
        >
          {label}
          <ChevronDownIcon className="top-nav-trigger__chevron" aria-hidden />
        </span>
      );
    }

    return (
      <Link
        to={link.href}
        onClick={() => {
          trackNavClick(link);
          handleNavigate();
        }}
      >
        {label}
      </Link>
    );
  };

  const navListDesktop = () => (
    <ul className="top-nav-list top-nav-list--desktop">
      {visibleLinks.map(link => (
        <li
          key={link.href}
          className={`nav-link-item${link.megaMenu ? ' top-nav-mega-item' : ''}`}
          onMouseEnter={link.megaMenu ? () => openMegaMenu(link.megaMenu!) : undefined}
          onMouseLeave={link.megaMenu ? scheduleCloseMegaMenu : undefined}
        >
          {renderPrimaryLink(link)}
        </li>
      ))}
    </ul>
  );

  return (
    <div
      className={`top-right-menu-root ${activeMega ? 'top-right-menu-root--mega-open' : ''}`}
      ref={topRightMenuRef}
    >
      <div className="site-header__nav-slot">
        <nav className="top-nav-desktop" aria-label="Main navigation">
          {navListDesktop()}
        </nav>

        <div className="top-nav-actions">
          {isAuthenticated ? (
            isDesktop ? (
              <div className="nav-user-dropdown-wrap" ref={userDropdownRef}>
                <button
                  type="button"
                  className="nav-user-dropdown-trigger"
                  onClick={() => setUserDropdownOpen(prev => !prev)}
                  aria-expanded={isUserDropdownOpen}
                  aria-haspopup="true"
                  aria-label="User menu"
                >
                  <UserCircleIcon className={`nav-icon h-8 w-8 ${navIconClass}`} />
                </button>
                {isUserDropdownOpen && (
                  <div className="nav-user-dropdown">
                    {userDisplay && <div className="nav-user-dropdown-info">{userDisplay}</div>}
                    <Link
                      to="/Profile-area"
                      className="nav-user-dropdown-item"
                      onClick={() => {
                        trackEvent({
                          name: 'navigation_click',
                          buttonId: 'navigation_route_profile_area',
                          label: 'Profile data',
                          page: 'global',
                          section: 'top_navigation',
                          feature: 'navigation',
                          eventCategory: 'navigation',
                        });
                        setUserDropdownOpen(false);
                      }}
                    >
                      Profile Data
                    </Link>
                    <button
                      type="button"
                      className="nav-user-dropdown-item nav-user-dropdown-logout"
                      onClick={() => {
                        setUserDropdownOpen(false);
                        handleLogout();
                      }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/Profile-area"
                className="top-nav-profile-link"
                aria-label="Profile"
                onClick={() =>
                  trackEvent({
                    name: 'navigation_click',
                    buttonId: 'navigation_route_profile_area',
                    label: 'Profile',
                    page: 'global',
                    section: 'top_navigation',
                    feature: 'navigation',
                    eventCategory: 'navigation',
                  })
                }
              >
                <UserCircleIcon className={`nav-icon h-8 w-8 ${navIconClass}`} />
              </Link>
            )
          ) : (
            location.pathname !== '/login' && (
              <Link
                to="/login"
                className="top-nav-profile-link"
                aria-label="Login"
                onClick={() =>
                  trackEvent({
                    name: 'navigation_click',
                    buttonId: 'navigation_route_login',
                    label: 'Login',
                    page: 'global',
                    section: 'top_navigation',
                    feature: 'navigation',
                    eventCategory: 'navigation',
                  })
                }
              >
                <UserCircleIcon className={`nav-icon h-8 w-8 ${navIconClass}`} />
              </Link>
            )
          )}

          <button
            type="button"
            className="hamburger"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
            onClick={() => {
              trackEvent({
                name: 'navigation_click',
                buttonId: 'navigation_toggle_mobile_menu',
                label: 'Toggle mobile menu',
                page: 'global',
                section: 'top_navigation',
                feature: 'navigation',
                eventCategory: 'navigation',
              });
              setMobileMenuOpen(prev => !prev);
            }}
          >
            <Bars3Icon className={`h-8 w-8 md:h-10 md:w-10 nav-icon ${navIconClass}`} />
          </button>
        </div>
      </div>

      {!isDesktop && (
        <MobileNavDrawer
          isOpen={isMobileMenuOpen}
          onClose={closeMobileMenu}
          links={visibleLinks}
          linkLabels={linkLabels}
          visibleTools={visibleTools}
          isAuthenticated={isAuthenticated}
          userDisplay={userDisplay}
          showLogin={location.pathname !== '/login'}
          onLogout={handleLogout}
        />
      )}

      <NavMegaPanel
        id="nav-mega-docs"
        variant="docs"
        isOpen={isDesktop && activeMega === 'docs'}
        onClose={closeMega}
        onHoverEnter={cancelCloseMegaMenu}
        onHoverLeave={scheduleCloseMegaMenu}
      >
        <DocsMegaMenu onNavigate={handleNavigate} />
      </NavMegaPanel>

      <NavMegaPanel
        id="nav-mega-tools"
        variant="tools"
        isOpen={isDesktop && activeMega === 'tools'}
        onClose={closeMega}
        onHoverEnter={cancelCloseMegaMenu}
        onHoverLeave={scheduleCloseMegaMenu}
      >
        <ToolsMegaMenu onNavigate={handleNavigate} />
      </NavMegaPanel>
    </div>
  );
};

export { TopRightMenu };
